import type {
	FocusEventHandler,
	KeyboardEventHandler,
	MouseEventHandler,
	ReactNode,
	Ref,
	SubmitEventHandler,
} from 'react';

import FocusTarget from 'components/FocusTarget';
import SearchField from 'components/SearchField';
import { useOutsideClick, useRefObject } from 'hooks';
import { cn, cnm } from 'utils/classNames';
import { getTestDataAttrFrom, is } from 'utils/helpers';

import {
	SearchComboboxContextProvider,
	useSearchComboboxContext,
} from './SearchComboboxContextProvider';

interface Props {
	/** Content after the entire combobox. */
	afterCombobox?: ReactNode;

	/** Content after the submit button. */
	afterSubmit?: ReactNode;

	/** Always show input clear button (when there is text) even if not open. */
	alwaysShowClearButton?: boolean;

	/** Search results. */
	children?: ReactNode;

	/** Container class name. */
	className?: string;

	/** Class name for the element containing the form fields. */
	controlsContainerClassName?: string;

	/** Field test ID */
	dataCy: string;

	/** Class name for the form element. */
	formClassName?: string;

	/** Search field id. */
	id?: string;

	/** Search field label. */
	inputLabel: string;

	/** Search field placeholder. */
	inputPlaceholder?: string;

	/** If the field should show a loading state. */
	isLoading?: boolean;

	/** Delay before the loading state is shown, in milliseconds. */
	loadingDelay?: number;

	/** Combobox close callback. */
	onClose?: () => void;

	/** Search form submit handler. */
	onFormSubmit?: SubmitEventHandler<HTMLFormElement>;

	/** Combobox option activation handler. Options with href are automatically navigated to. */
	onOptionSelect?: (option: HTMLElement) => void;

	/** Container class name set only when the combobox is open. */
	openClassName?: string;

	/** Ref for the container element. */
	ref?: Ref<HTMLDivElement>;

	/** Class name for the element containing the results component. */
	resultsContainerClassName?: string;

	/** Minimum number of characters required for a search. */
	searchQueryMinLength?: number;

	/** Form submit button label. */
	submitButtonLabel: string;
}

/**
 * Use with a manually rendered context provider if the combobox needs data
 * that would otherwise only be available in the child components.
 *
 * @example
 *
 * // The combobox here needs the loading state from the search function.
 * function MySearchCombobox() {
 *   const { listboxProps, searchQuery } = useSearchComboboxContext();
 *   const { isLoading, results } = search(searchQuery);
 *
 *   return (
 *     <SearchCombobox isLoading={isLoading}>
 *       <div {...listboxProps}>
 *         <ComboboxOptionGroup>
 *           {results.map((result) => (
 *             <ComboboxOption>{result...}</ComboboxOption>
 *           ))}
 *         </ComboboxOptionGroup>
 *       </div>
 *     </SearchCombobox>
 *   );
 * }
 *
 * function MySearch() {
 *   return (
 *     <SearchComboboxContextProvider>
 *       <MySearchCombobox />
 *     </SearchComboboxContextProvider>
 *   );
 * }
 */
export function SearchComboboxWithoutContext({
	afterCombobox,
	afterSubmit,
	alwaysShowClearButton,
	children,
	className,
	controlsContainerClassName,
	dataCy,
	formClassName,
	inputLabel,
	inputPlaceholder,
	isLoading,
	loadingDelay,
	onFormSubmit,
	openClassName,
	ref,
	resultsContainerClassName,
	submitButtonLabel,
}: Props) {
	const {
		afterFormRef,
		baseId: id,
		blurInput,
		clearSelectedOption,
		closeCombobox,
		focusInput,
		getFormSubmitHandler,
		inputProps,
		inputRef,
		isOpen,
		rawInputValue: inputValue,
		setInputValue,
	} = useSearchComboboxContext();

	const containerRef = useRefObject<HTMLDivElement>(ref);

	// Close search dropdown on click outside
	useOutsideClick(containerRef, () => {
		closeCombobox();
	});

	// Close search dropdown when focus leaves the form (e.g. tabbing away)
	const handleFormBlur: FocusEventHandler<HTMLFormElement> = (e) => {
		if (
			is.element(e.relatedTarget) &&
			is.element(e.currentTarget) &&
			!e.currentTarget.contains(e.relatedTarget)
		) {
			closeCombobox();
			blurInput();
		}
	};

	// Clear selection and send focus to input on Escape press.
	const handleFormKeyDown: KeyboardEventHandler<HTMLFormElement> = (e) => {
		if (e.key === 'Escape') {
			clearSelectedOption();
			// Leave focus on the submit button if pressing escape there.
			if (
				is.instance(e.target, HTMLButtonElement) &&
				e.target.type === 'submit'
			) {
				closeCombobox();
			} else {
				focusInput();
			}
		}
	};

	const handleFormSubmit: SubmitEventHandler<HTMLFormElement> = (e) => {
		e.preventDefault();
		onFormSubmit?.(e);
		getFormSubmitHandler()?.();
	};

	const handleInputClearClick: MouseEventHandler<HTMLButtonElement> = (e) => {
		setInputValue('');
		// Not focusing the input when clicking the clear button with a mouse
		// while the combobox is closed.
		const isKeyboard = !e.screenX && !e.screenY;
		if (isOpen || isKeyboard) {
			focusInput();
		}
	};

	const hasInputClearButton = alwaysShowClearButton
		? inputValue.length > 0
		: isOpen && inputValue.length > 0;

	return (
		<>
			<div
				ref={containerRef}
				className={cn(className, isOpen && openClassName)}
			>
				{/* This keydown event captures bubbling escape presses from interactive elements. */}
				{/* oxlint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
				<form
					onBlur={handleFormBlur}
					onKeyDown={handleFormKeyDown}
					onSubmit={handleFormSubmit}
					className={cnm('relative flex w-full', formClassName)}
				>
					<div className={cnm('z-2 flex w-full', controlsContainerClassName)}>
						<SearchField
							id={id}
							ref={inputRef}
							inputLabel={inputLabel}
							placeholder={inputPlaceholder}
							onInputClearClick={
								hasInputClearButton ? handleInputClearClick : undefined
							}
							submitButtonLabel={submitButtonLabel}
							isLoading={isLoading}
							loadingDelay={loadingDelay}
							loadingStyle="field"
							data-cy={getTestDataAttrFrom(dataCy)}
							// Reduce input focus outline. The results container will appear
							// on focus (making focus quite clear) and the closeness of its
							// edges creates a striped effect with the default outline.
							inputClassName="focus-outline-none focus:outline-1 focus:outline-offset-0 focus:outline-focus focus:outline-solid"
							{...inputProps}
						/>
						{afterSubmit}
					</div>

					<div
						className={cnm(
							'absolute -inset-2 bottom-auto z-1',
							'overflow-y-auto overscroll-contain',
							'px-2 pt-14 pb-2',
							'bg-base',
							'rounded-sm',
							'shadow-xl forced-colors-outline',
							resultsContainerClassName,
							!isOpen && 'hidden',
						)}
					>
						{children}
					</div>
				</form>

				<FocusTarget ref={afterFormRef} />
			</div>

			{afterCombobox}
		</>
	);
}
SearchComboboxWithoutContext.displayName = 'SearchComboboxWithoutContext';

/**
 * A search field implementing the combobox pattern.
 *
 * - https://www.w3.org/WAI/ARIA/apg/patterns/combobox/
 * - https://www.w3.org/WAI/ARIA/apg/patterns/listbox/examples/listbox-grouped/
 *
 * The results children should render ComboboxOption wrapped in a listbox,
 * possibly with ComboboxOptionGroup. State from the combobox is available
 * through the `useSearchComboboxContext` hook.
 *
 * @example
 *
 * function MyResults() {
 *   const { listboxProps, searchQuery } = useSearchComboboxContext();
 *   const results = search(searchQuery);
 *
 *   return (
 *     <div {...listboxProps}>
 *       <ComboboxOptionGroup>
 *         {results.map((result) => (
 *           <ComboboxOption>{result...}</ComboboxOption>
 *         ))}
 *       </ComboboxOptionGroup>
 *     </div>
 *   );
 * }
 *
 * function MySearch() {
 *   return (
 *     <SearchCombobox>
 *       <MyResults />
 *     </SearchCombobox>
 *   );
 * }
 */
export default function SearchCombobox({
	id,
	onClose,
	onOptionSelect,
	ref,
	searchQueryMinLength,
	...comboboxProps
}: Props) {
	return (
		<SearchComboboxContextProvider
			id={id}
			onClose={onClose}
			onOptionSelect={onOptionSelect}
			searchQueryMinLength={searchQueryMinLength}
		>
			<SearchComboboxWithoutContext ref={ref} {...comboboxProps} />
		</SearchComboboxContextProvider>
	);
}
SearchCombobox.displayName = 'SearchCombobox';
